import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Smartphone, LogIn, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { settingsApi } from '@/lib/store';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const s = settingsApi.get();

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username.trim(), password);
      toast.success('Login berhasil');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background">
      {/* Visual side */}
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/6754839/pexels-photo-6754839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Repair"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/70 to-primary/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            {s.logo_url ? (
              <img src={s.logo_url} alt="Logo" className="h-11 w-11 rounded-md object-contain bg-white/15 border border-white/20 p-1" />
            ) : (
              <div className="h-11 w-11 rounded-md bg-white/15 backdrop-blur grid place-items-center border border-white/20">
                <Smartphone className="h-6 w-6" />
              </div>
            )}
            <div>
              <div className="font-display font-bold text-xl tracking-tight">{s.shop_name}</div>
              <div className="text-xs text-white/70 mt-0.5">{s.shop_tagline || 'Manajemen Servis HP'}</div>
            </div>
          </div>

          <div className="space-y-6">
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]">
              Servis HP jadi rapi,<br />pelanggan senang, laba jelas.
            </h1>
            <p className="text-white/80 text-lg max-w-lg leading-relaxed">
              Kelola tiket servis, pelanggan, sparepart, dan laporan keuangan cabang dari satu dashboard — cepat, ringkas, dan siap dipakai kasir hari ini.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4 max-w-lg">
              {[
                { k: 'Tiket', v: 'RBAC' },
                { k: 'Sparepart', v: 'Realtime' },
                { k: 'Notifikasi', v: 'WhatsApp' },
              ].map((s) => (
                <div key={s.k}>
                  <div className="text-xs text-white/60">{s.k}</div>
                  <div className="font-display font-semibold text-lg">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md space-y-8 animate-slide-up">
          <div className="lg:hidden flex items-center gap-2.5">
            {s.logo_url ? (
              <img src={s.logo_url} alt="Logo" className="h-10 w-10 rounded-md object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
                <Smartphone className="h-5 w-5" />
              </div>
            )}
            <div>
              <div className="font-display font-bold">{s.shop_name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.shop_tagline}</div>
            </div>
          </div>

          <div>
            <div className="text-sm text-muted-foreground mb-2">Selamat datang</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Masuk ke akun Anda</h2>
            <p className="text-muted-foreground mt-2 text-sm">Gunakan kredensial staf Anda untuk melanjutkan.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Username</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="login-username"
                className="w-full h-11 px-3.5 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                placeholder="admin / teknisi / kasir"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full h-11 pl-3.5 pr-11 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center text-muted-foreground hover:text-foreground rounded-md hover:bg-accent transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-11 rounded-md bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <LogIn className="h-4 w-4" />
              {busy ? 'Memproses...' : 'Masuk'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
